// מחולל קישורים מתויגים — דטרמיניסטי, ללא AI.

export interface TrackingTemplate {
  version: number;
  source: string;   // תבנית: {media} {campaign} {banner} {size} {variant}
  medium: string;
  campaign: string;
  content: string;
}

export const DEFAULT_TEMPLATE: TrackingTemplate = {
  version: 1,
  source: '{media}',
  medium: 'display',
  campaign: '{campaign}',
  content: '{banner}_{size}',
};

export interface TrackingInput {
  baseUrl: string;
  campaignKey: string;
  mediaKey: string;
  bannerKey: string;
  width: number;
  height: number;
  variantId?: string;
  /** suffix מזהה וריאציה כשיש כפילות שם+מידה */
  disambiguate?: boolean;
}

export type ExistingUtmPolicy = 'ask' | 'replace' | 'keep';

export interface TrackingResult {
  resolvedUrl: string | null;
  params: Record<string, string>;
  /** UTM שכבר קיים ב-URL ושונה מהמחושב — דורש בחירת משתמש */
  conflicts: { key: string; existing: string; generated: string }[];
  warnings: string[];
  errors: string[];
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

/** מפתח מעקב יציב מתוך שם תצוגה. עברית נשארת ב-displayName; ה-key לטיני. */
export function slugifyKey(input: string): string {
  const s = input
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[֑-ׇ]/g, '') // ניקוד
    .replace(/[^a-z0-9א-ת]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const latin = transliterateHebrew(s).replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  return latin.slice(0, 60) || 'item';
}

const HEB: Record<string, string> = {
  'א': 'a', 'ב': 'b', 'ג': 'g', 'ד': 'd', 'ה': 'h', 'ו': 'v', 'ז': 'z', 'ח': 'ch', 'ט': 't', 'י': 'y',
  'כ': 'k', 'ך': 'k', 'ל': 'l', 'מ': 'm', 'ם': 'm', 'נ': 'n', 'ן': 'n', 'ס': 's', 'ע': 'a', 'פ': 'p',
  'ף': 'f', 'צ': 'tz', 'ץ': 'tz', 'ק': 'k', 'ר': 'r', 'ש': 'sh', 'ת': 't',
};
function transliterateHebrew(s: string): string {
  return [...s].map((c) => HEB[c] ?? c).join('');
}

const KEY_RE = /^[a-z0-9][a-z0-9_-]{0,79}$/;

function fill(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}

export function buildTrackingUrl(
  input: TrackingInput,
  template: TrackingTemplate = DEFAULT_TEMPLATE,
  existingPolicy: ExistingUtmPolicy = 'ask',
): TrackingResult {
  const res: TrackingResult = { resolvedUrl: null, params: {}, conflicts: [], warnings: [], errors: [] };
  for (const [name, v] of [['campaignKey', input.campaignKey], ['mediaKey', input.mediaKey], ['bannerKey', input.bannerKey]] as const) {
    if (!KEY_RE.test(v)) res.errors.push(`${name} לא תקין: "${v}" — אותיות לטיניות קטנות, ספרות, - ו-_ בלבד`);
  }
  if (!Number.isInteger(input.width) || !Number.isInteger(input.height) || input.width <= 0 || input.height <= 0) {
    res.errors.push('מידות לא תקינות');
  }
  let url: URL;
  try {
    url = new URL(input.baseUrl.trim());
  } catch {
    res.errors.push('כתובת בסיס לא תקינה');
    return res;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') res.errors.push('רק http/https');
  if (url.protocol === 'http:') res.warnings.push('הכתובת אינה https');
  if (res.errors.length) return res;

  const size = `${input.width}x${input.height}`;
  const banner = input.disambiguate && input.variantId ? `${input.bannerKey}-${input.variantId.slice(0, 8)}` : input.bannerKey;
  const vars = { media: input.mediaKey, campaign: input.campaignKey, banner, size, variant: input.variantId ?? '' };
  const gen: Record<string, string> = {
    utm_source: fill(template.source, vars),
    utm_medium: fill(template.medium, vars),
    utm_campaign: fill(template.campaign, vars),
    utm_content: fill(template.content, vars),
  };

  // query חתום: שינוי עלול לשבור חתימה
  const signedHints = ['sig', 'signature', 'token', 'hmac', 'x-amz-signature', 'expires'];
  for (const k of url.searchParams.keys()) {
    if (signedHints.includes(k.toLowerCase())) { res.warnings.push(`הכתובת מכילה פרמטר חתום (${k}) — הוספת UTM עשויה לפגוע בחתימה. יש לבדוק ידנית.`); break; }
  }

  for (const k of UTM_KEYS) {
    const all = url.searchParams.getAll(k);
    const existing = all[0];
    if (existing !== undefined && existing !== gen[k]) {
      if (existingPolicy === 'ask') { res.conflicts.push({ key: k, existing, generated: gen[k] }); continue; }
      if (existingPolicy === 'keep') { gen[k] = existing; }
    }
    url.searchParams.delete(k); // אין כפילות באותו מפתח
    url.searchParams.set(k, gen[k]);
  }
  if (res.conflicts.length) return { ...res, params: gen };
  res.params = gen;
  res.resolvedUrl = url.toString(); // URLSearchParams מקודד Unicode ורווחים; fragment נשמר
  return res;
}

/** מסמן כפילויות שם+מידה באותה מדיה כדי להוסיף suffix */
export function findDuplicates(rows: { variantId: string; mediaKey: string; bannerKey: string; width: number; height: number }[]): Set<string> {
  const seen = new Map<string, string[]>();
  for (const r of rows) {
    const k = `${r.mediaKey}|${r.bannerKey}|${r.width}x${r.height}`;
    seen.set(k, [...(seen.get(k) ?? []), r.variantId]);
  }
  const dup = new Set<string>();
  for (const ids of seen.values()) if (ids.length > 1) ids.forEach((i) => dup.add(i));
  return dup;
}
